import React, { useCallback, useMemo, useState } from "react";

// i18n
import { useTranslation } from "react-i18next";

// MUI
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

// Types
import { OnboardingData, Section, Step } from "../types";
import { getOptions } from "../utils";

// Styles
import styles from './style.module.scss';
import { useOnboardingContext } from "../context";

type StepProps = {
  step: Step,
  section: Section,
  stepIndex: number,
  totalSteps: number
  onNext: () => void,
  onSkip: () => void
}

const StepComponent: React.FC<StepProps> = ({
  step,
  section,
  stepIndex,
  totalSteps,
  onNext,
  onSkip
}) => {
  // i18n
  const { t } = useTranslation();

  // Context
  const { setOnboardingData } = useOnboardingContext();

  // State
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [customOption, setCustomOption] = useState<string>('');

  // Memoized
  const options = useMemo(() => {
    return getOptions(section, step.id, t)
  }, [section, step])

  // Handlers
  const handleOptionSelect = useCallback((option: string) => {
    setSelectedOptions((prev) => {
      // If step doesn't allow multiple selection, replace the selection
      if (!step.multiple) {
        return prev.includes(option) ? [] : [option];
      }

      // For multiple selection, toggle the option
      if (prev.includes(option)) {
        return prev.filter((o) => o !== option);
      }

      return [...prev, option];
    });
  }, [step.multiple]);

  const handleCustomOptionChange = useCallback((value: string) => {
    setCustomOption(value);

    // If step doesn't allow multiple selection, clear other selections when custom option is entered
    if (!step.multiple && value.trim()) setSelectedOptions([]);
  }, [step.multiple]);

  const hasSelection = selectedOptions.length > 0 || customOption.trim().length > 0;

  const handleNext = useCallback(() => {
    setOnboardingData((prev) => {
      const newData = { ...prev };
      newData[section] = { ...newData[section] };
      newData[section][step.id] = selectedOptions.length > 0 ? selectedOptions : customOption.trim();
      return newData;
    });

    setSelectedOptions([]);
    setCustomOption('');
    onNext();
  }, [onNext, step.id, section, selectedOptions, customOption, setOnboardingData]);

  const handleSkip = useCallback(() => {
    // Save skipped step data with a default value
    setOnboardingData((prev) => {
      const newData = { ...prev };
      newData[section] = { ...newData[section] };
      newData[section][step.id] = null; // Mark as skipped
      return newData;
    });

    // Reset local state
    setSelectedOptions([]);
    setCustomOption('');

    onSkip();
  }, [onSkip, step.id, section, setOnboardingData]);

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <Typography variant="body1" component="p">
          <Typography variant="body1" component="span" color="text.secondary" sx={{ marginRight: 1 }}>
            {stepIndex} / {totalSteps}
          </Typography>

          {step.title}
        </Typography>

        {step.helpText && (
          <Typography variant="body1" component="p" color="text.secondary">
            {step.helpText}
          </Typography>
        )}
      </div>

      <div className={styles.stepOptions}>
        {options.map((option) => {
          const isDisabled = !step.multiple && customOption.trim().length > 0;
          const isSelected = selectedOptions.includes(option.value);

          return (
            <Button
              key={option.value}
              classes={{
                root: styles.stepOption,
                contained: isSelected ? styles.stepOption_selected : '',
              }}
              onClick={() => handleOptionSelect(option.value)}
              variant={isSelected ? 'contained' : 'outlined'}
              color="primary"
              disabled={isDisabled}
            >
              {option.label}
            </Button>
          );
        })}

        {step.allowCustom && (
          <TextField
            fullWidth
            size="small"
            placeholder={t('onboarding.common.custom_option')}
            value={customOption}
            onChange={(e) => handleCustomOptionChange(e.target.value)}
          />
        )}
      </div>

      <div className={styles.stepActions}>
        <Button variant="contained" disabled={!hasSelection} color="primary" onClick={handleNext}>
          {t('actions.next')}
        </Button>

        <Button variant="outlined" color="primary" onClick={handleSkip}>
          {t('actions.skip_question')}
        </Button>
      </div>
    </div>
  )
}

export default StepComponent;
